/**
 * The app shell: navigation, the folder tree, and the upload banner.
 *
 * Everything here is outside the router's Outlet, which is what lets an upload
 * survive navigation. On a phone the sidebar becomes a drawer; it is the same
 * markup either way, so there is no second mobile navigation to keep in step.
 */
import { useState, type ReactNode } from 'react';
import { Link, useRouterState } from '@tanstack/react-router';
import { Dialog } from '@base-ui/react/dialog';
import {
  FolderTree as FolderTreeIcon,
  HardDrive,
  Images,
  LogOut,
  Menu as MenuIcon,
  ScrollText,
  Shield,
  Users,
  X,
} from 'lucide-react';
import { api } from '../api/client';
import { useIncomingShares } from '../api/hooks';
import { FolderTree } from './FolderTree';
import { UploadBanner } from './Uploader';
import { Button, cx } from './ui';
import type { UserDto } from '../api/types';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  badge?: number;
}

function NavLinks({ user, onNavigate }: { user: UserDto; onNavigate?: () => void }) {
  const incoming = useIncomingShares();
  const sharedCount = incoming.data?.items.length ?? 0;

  const items: NavItem[] = [
    { to: '/', label: 'My files', icon: <HardDrive className="size-4" /> },
    {
      to: '/shared',
      label: 'Shared with me',
      icon: <Users className="size-4" />,
      badge: sharedCount || undefined,
    },
    { to: '/collections', label: 'Albums', icon: <Images className="size-4" /> },
  ];

  const adminItems: NavItem[] = user.isAdmin
    ? [
        { to: '/admin/files', label: 'All files', icon: <Shield className="size-4" /> },
        { to: '/admin/users', label: 'Accounts', icon: <Users className="size-4" /> },
        { to: '/admin/logs', label: 'Activity log', icon: <ScrollText className="size-4" /> },
      ]
    : [];

  const renderItem = (item: NavItem) => (
    <Link
      key={item.to}
      to={item.to}
      onClick={onNavigate}
      activeOptions={{ exact: item.to === '/' }}
      className="flex items-center gap-2.5 rounded-lg px-2.5 py-2 text-sm hover:bg-surface-2 data-[status=active]:bg-brand/15 data-[status=active]:font-medium"
    >
      <span className="text-ink-dim">{item.icon}</span>
      <span className="flex-1 truncate">{item.label}</span>
      {item.badge ? (
        <span className="rounded-full bg-brand/20 px-1.5 text-[11px] font-medium text-brand tabular-nums">
          {item.badge}
        </span>
      ) : null}
    </Link>
  );

  return (
    <nav className="flex flex-col gap-0.5">
      {items.map(renderItem)}
      {adminItems.length > 0 ? (
        <>
          <p className="mt-4 mb-1 px-2.5 text-[11px] font-medium tracking-wide text-ink-dim uppercase">
            Admin
          </p>
          {adminItems.map(renderItem)}
        </>
      ) : null}
    </nav>
  );
}

function SidebarContent({
  user,
  activeFolderId,
  selectedFileIds,
  onNavigate,
}: {
  user: UserDto;
  activeFolderId: string | null;
  selectedFileIds: string[];
  onNavigate?: () => void;
}) {
  return (
    <div className="flex h-full min-h-0 flex-col gap-4 p-3">
      <NavLinks user={user} onNavigate={onNavigate} />
      <div className="flex min-h-0 flex-1 flex-col">
        <p className="mb-1 flex items-center gap-1.5 px-2.5 text-[11px] font-medium tracking-wide text-ink-dim uppercase">
          <FolderTreeIcon className="size-3" aria-hidden />
          Folders
        </p>
        <FolderTree
          myUserId={user.id}
          activeFolderId={activeFolderId}
          draggedFileIds={selectedFileIds}
        />
      </div>
    </div>
  );
}

export function Layout({
  user,
  selectedFileIds,
  children,
}: {
  user: UserDto;
  /** Ticked files, so dropping onto a tree folder can move them. */
  selectedFileIds: string[];
  children: ReactNode;
}) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const routerState = useRouterState();
  const activeFolderId =
    (routerState.matches.at(-1)?.params as { folderId?: string } | undefined)?.folderId ?? null;

  const signOut = async () => {
    await api.post('/api/logout').catch(() => undefined);
    window.location.assign('/');
  };

  return (
    <div className="flex h-dvh flex-col">
      <header className="flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-3">
        <Button
          icon
          variant="ghost"
          aria-label="Open navigation"
          className="lg:hidden"
          onClick={() => setDrawerOpen(true)}
        >
          <MenuIcon className="size-5" />
        </Button>

        <Link to="/" className="flex items-center gap-2 font-semibold">
          <span className="grid size-7 place-items-center rounded-lg bg-brand text-brand-ink">
            W
          </span>
          <span className="hidden sm:inline">Wood Family Files</span>
        </Link>

        <div className="flex-1" />

        <span className="hidden text-sm text-ink-dim sm:inline">{user.displayName}</span>
        <Button icon variant="ghost" aria-label="Sign out" onClick={signOut}>
          <LogOut className="size-5" />
        </Button>
      </header>

      <div className="flex min-h-0 flex-1">
        <aside className="hidden w-64 shrink-0 overflow-hidden border-r border-line bg-surface lg:flex lg:flex-col">
          <SidebarContent
            user={user}
            activeFolderId={activeFolderId}
            selectedFileIds={selectedFileIds}
          />
        </aside>

        <main className="min-w-0 flex-1 overflow-y-auto">{children}</main>
      </div>

      <UploadBanner />

      <Dialog.Root open={drawerOpen} onOpenChange={setDrawerOpen}>
        <Dialog.Portal>
          <Dialog.Backdrop className="fixed inset-0 z-40 bg-black/40 transition-opacity data-[starting-style]:opacity-0 data-[ending-style]:opacity-0" />
          <Dialog.Popup
            className={cx(
              'fixed inset-y-0 left-0 z-50 flex w-[min(19rem,85vw)] flex-col bg-surface shadow-2xl',
              'transition-transform data-[starting-style]:-translate-x-full data-[ending-style]:-translate-x-full',
            )}
          >
            <div className="flex h-14 shrink-0 items-center justify-between border-b border-line px-3">
              <Dialog.Title className="font-semibold">Menu</Dialog.Title>
              <Dialog.Close
                aria-label="Close navigation"
                className="rounded-lg p-2 text-ink-dim hover:bg-surface-2"
              >
                <X className="size-5" />
              </Dialog.Close>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto">
              <SidebarContent
                user={user}
                activeFolderId={activeFolderId}
                selectedFileIds={selectedFileIds}
                onNavigate={() => setDrawerOpen(false)}
              />
            </div>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  );
}
